import React, { useEffect, useCallback, useState } from 'react';
import { SignIn } from '@clerk/clerk-react';
import WaitlistSignupForm from './WaitlistSignupForm';

export interface SignInModalProps {
  isOpen: boolean;
  onClose: () => void;
  returnUrl?: string;
  afterSignInUrl?: string;
  afterSignUpUrl?: string;
}

interface SignupAvailability {
  available: boolean;
  spotsRemaining: number;
  totalLimit: number;
  currentCount: number;
}

const SignInModal: React.FC<SignInModalProps> = ({
  isOpen,
  onClose,
  returnUrl,
  afterSignInUrl,
  afterSignUpUrl,
}) => {
  const [availability, setAvailability] = useState<SignupAvailability | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showMode, setShowMode] = useState<'signin' | 'waitlist'>('signin');

  // Check signup availability when modal opens
  useEffect(() => {
    if (isOpen) {
      checkAvailability();
    }
  }, [isOpen]);

  const checkAvailability = async () => {
    try {
      setIsLoading(true);
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/api/public/signup-availability`);
      const data = await response.json();
      setAvailability(data);
      
      // If signups are not available, show waitlist form by default
      if (!data.available) {
        setShowMode('waitlist');
      }
    } catch (error) {
      console.error('Failed to check signup availability:', error);
      // On error, allow signin (fail open for existing users)
      setAvailability({ available: true, spotsRemaining: 0, totalLimit: 0, currentCount: 0 });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle escape key to close modal
  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  // Build redirect URLs
  const redirectUrl = returnUrl || window.location.href;
  const signInUrl = afterSignInUrl || redirectUrl;
  const signUpUrl = afterSignUpUrl || redirectUrl;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="signin-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Content */}
      <div className="relative z-10 animate-fade-in">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 z-20 p-2 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors shadow-lg"
          aria-label="Close sign in modal"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Loading State */}
        {isLoading && (
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-slate-400">Checking availability...</p>
          </div>
        )}

        {/* Waitlist Mode - Signups Full */}
        {!isLoading && showMode === 'waitlist' && !availability?.available && (
          <div className="max-w-md">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 mb-4 text-center">
              <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-yellow-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Signups Currently Full</h3>
              <p className="text-slate-400 text-sm mb-4">
                We've reached our capacity of {availability?.totalLimit} users. 
                {availability && availability.currentCount > 0 && (
                  <> Currently {availability.currentCount} active users.</>
                )}
              </p>
              <button
                onClick={() => setShowMode('signin')}
                className="text-sm text-blue-400 hover:text-blue-300 underline"
              >
                Already have an account? Sign in here
              </button>
            </div>
            <WaitlistSignupForm onSuccess={onClose} />
          </div>
        )}

        {/* Sign In Mode - Clerk UI */}
        {!isLoading && (showMode === 'signin' || availability?.available) && (
          <>
            {/* Show warning if at capacity but allowing existing users to sign in */}
            {!availability?.available && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4 max-w-md">
                <p className="text-sm text-yellow-300 text-center">
                  <strong>Note:</strong> New signups are currently closed. Existing users can still sign in.
                </p>
              </div>
            )}
            
            <SignIn
              afterSignInUrl={signInUrl}
              afterSignUpUrl={signUpUrl}
              redirectUrl={redirectUrl}
              signUpUrl={availability?.available ? undefined : '#'} // Disable signup if not available
              appearance={{
                elements: {
                  rootBox: 'mx-auto',
                  card: 'bg-slate-900 border border-slate-700 shadow-2xl',
                  headerTitle: 'text-white',
                  headerSubtitle: 'text-slate-400',
                  socialButtonsBlockButton:
                    'bg-slate-800 border-slate-600 text-white hover:bg-slate-700',
                  socialButtonsBlockButtonText: 'text-white',
                  dividerLine: 'bg-slate-700',
                  dividerText: 'text-slate-500',
                  formFieldLabel: 'text-slate-300',
                  formFieldInput:
                    'bg-slate-800 border-slate-600 text-white placeholder:text-slate-500',
                  formButtonPrimary:
                    'bg-gradient-to-r from-emerald-500 via-blue-500 to-purple-500 hover:from-emerald-400 hover:via-blue-400 hover:to-purple-400',
                  footerActionLink: 'text-emerald-400 hover:text-emerald-300',
                  identityPreviewText: 'text-white',
                  identityPreviewEditButton: 'text-emerald-400',
                },
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default SignInModal;
